/**
 * Stores > Item Sales (/retail/item-sales).
 *
 * A SKU-level sales grid: one row per store per SKU for the whole selected date
 * range, showing units sold, net sales, cost, and ticket count. It answers
 * "what sold, at which store, over which days, by SKU", which neither
 * Allocation (units only, ~151 transfer SKUs) nor the Revenue Leakage explorer
 * (rolled up by category, no store dimension on its fast path) can answer.
 *
 * Rows are aggregated ACROSS the range, so a week-long view shows one line per
 * store per SKU with the week's totals, not seven lines.
 *
 * Filters, in bar order: a from date and a to date (both default to yesterday);
 * an "Items included" selector that defaults to merchandise and discounts and
 * therefore EXCLUDES bag fees (one bag-fee SKU carried 306,471 units at $0 over
 * three months and swamped the units column, Steven 2026-08-04); a store picker
 * (about 50 options, so it uses the shared SearchableSelect and gets a search
 * bar per COCKPIT.md 2.8); cascading Category and Subcategory pickers (14 and 20
 * options, under the 15-option threshold, so no search bar); and a
 * SKU-or-description text field (the `q` param). A Download button pulls the
 * same rows as Excel through the export endpoint, so the workbook always
 * matches the view.
 *
 * The two taxonomy pickers cascade: choosing a category narrows the subcategory
 * menu to that category's subcategories, and any subcategory selection the new
 * category no longer offers is dropped from the URL so an invisible filter
 * cannot silently empty the grid. Both are sourced from the response's
 * `categories` array, which the backend does NOT narrow by the current filters,
 * so choosing one category never hides the other 13.
 *
 * Columns, in order: Store, SKU, Description, Category, Subcategory, Units
 * sold, Net sales, Cost, Tickets. Every column sorts through the shared
 * SortableHeader, and the sort is sent to the backend rather than applied
 * client-side, because re-ordering only the pages loaded so far would break
 * infinite scroll. Money and units are right-aligned. The SKU cell links to the
 * existing product detail page.
 *
 * Cost and Tickets are nullable per row and render as "--" when absent. The two
 * online (DTC) stores have no per-SKU ticket count, and cost only exists where
 * cost-per-item is populated in Shopify. The spec requires NULL there rather
 * than a 0 that would read as a real zero inside a sum.
 *
 * A Subscribe control under the header signs the caller up for this report by
 * email every morning, saving the filters currently applied. The date range is
 * deliberately excluded from what is saved: every email covers the most recent
 * complete business day, worked out fresh each morning. When the caller already
 * holds a subscription saved with exactly these filters, the same button
 * removes it.
 *
 * Filter state lives in the URL query string, so a filtered view is a shareable
 * link and a browser reload keeps the view. A URL carrying the first version's
 * single `date` param still works: it is read into both ends of the range.
 *
 * RBAC: gated on `retail.itemsales.read`. Holding it grants the FLEET view
 * (Steven, 2026-08-04); there is no store-manager own-store path, so `store_no`
 * is a filter and never a scope boundary. Scope is enforced server-side.
 *
 * Resilience: the backend endpoint ships in parallel (api repo, spec Tasks 4
 * and 5), so the page is wrapped in an ErrorBoundary, reads every field
 * defensively through useItemSales, and renders a quiet "not available yet"
 * state on any 404 / 503 / shape mismatch instead of crashing.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, Download, Loader2, Mail, MailCheck } from 'lucide-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { FilterLabel } from '@/components/FilterLabel';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { SearchableSelect } from '@/components/SearchableSelect';
import { InfoTooltip, SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { useRetailStores } from '@/lib/useRetailStores';
import { formatDate } from '@/lib/dateUtils';
import { useReportPageState } from '@/lib/reportPageState';
import {
  ITEM_SALES_REPORT_TYPE,
  useCreateReportSubscription,
  useDeleteReportSubscription,
  useReportSubscriptions,
} from '@/lib/useReportSubscriptions';
import {
  buildItemSalesPageState,
  downloadItemSalesXlsx,
  formatItemMoney,
  formatItemSalesSort,
  formatItemStore,
  formatItemTickets,
  formatItemUnits,
  itemSalesCategoryNames,
  itemSalesFiltersKey,
  itemSalesSubcategoryNames,
  itemSalesSubscriptionFilters,
  itemTypeCodesFor,
  itemTypeLabelFor,
  itemTypePresetFromParam,
  parseItemSalesSort,
  pruneItemSalesSubcategories,
  resolveItemSalesRange,
  DEFAULT_ITEM_TYPE_PRESET,
  ITEM_SALES_SORT_OPTIONS,
  ITEM_TYPE_PRESETS,
  useItemSales,
  type ItemSalesFilters,
  type ItemSalesSort,
  type ItemSalesSortColumn,
  type ItemTypePreset,
} from '@/lib/useItemSales';

/** The accessSlug that grants this report. Its own, independently grantable
 * (spec 00_access-binary-grants.md, 2026-08-09; renamed from
 * retail.itemsales.read). */
const ITEM_SALES_CAPABILITY = 'stores.itemsales';

const COLUMN_COUNT = 9;

/**
 * The one place the bag-fee exclusion is explained, so the tooltip and the
 * helper line under the selector cannot drift apart (COCKPIT.md 2.3).
 */
const ITEM_TYPE_HELP =
  'CounterPoint tags every sale as merchandise, a discount, or a non-inventory charge. ' +
  'Bag fees, gift wrap, and shipping are non-inventory, and they are excluded by default ' +
  'because they distort the units column: one bag-fee item alone carried 306,471 units at ' +
  '$0 over three months. Switch to "Everything, including bag fees" to see them.';

// ---------------------------------------------------------------------------
// RBAC
// ---------------------------------------------------------------------------

/**
 * Holding stores.itemsales is the whole gate, and it confers the fleet view.
 * Unlike OMNI Performance and Revenue Leakage there is no store-manager
 * own-store fallback, because Steven owns who gets granted this report
 * (decision 2, 2026-08-04). The backend enforces the same rule and 403s a
 * caller without the accessSlug.
 */
function canViewItemSales(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes(ITEM_SALES_CAPABILITY);
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Yesterday (local), the latest complete business day and both range defaults. */
function defaultDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toIsoDate(d);
}

// ---------------------------------------------------------------------------
// Presentational atoms
// ---------------------------------------------------------------------------


function AccessDeniedCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        Item Sales is gated by the {ITEM_SALES_CAPABILITY} capability.
      </p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Ask IT to grant you access.
      </p>
    </div>
  );
}

function NotAvailableCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">Not available yet.</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        There are no item sales in this date range. Widen the range, or check back once the nightly
        sales load has run.
      </p>
    </div>
  );
}

function EmptyBody({ message }: { message: string }) {
  return (
    <tr>
      <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {message}
      </td>
    </tr>
  );
}

/** A taxonomy cell: the name, or a muted note when the SKU carries none. */
function TaxonomyCell({ value, kind }: { value: string | null; kind: 'category' | 'subcategory' }) {
  if (value) return <>{value}</>;
  return (
    <span
      className="text-amp-ink-muted dark:text-amp-dark-ink-muted"
      title={`This item has no ${kind} in the product taxonomy.`}
    >
      --
    </span>
  );
}

// ---------------------------------------------------------------------------
// Mobile cards
// ---------------------------------------------------------------------------

/**
 * One labelled number inside a card. Nine table columns do not fit a phone, so
 * the four measures drop out of the row into a two-by-two block where each
 * value keeps its own label. Same "--" for a missing cost or ticket count that
 * the table shows, through the same formatters, so a card and a row can never
 * disagree about what a blank means.
 */
function CardMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="text-[14px] font-semibold text-amp-ink dark:text-amp-dark-ink">{value}</dd>
    </div>
  );
}

/** Loading / error / empty copy in card mode, where there is no table row to fill. */
function CardNotice({ message }: { message: string }) {
  return (
    <p className="px-3 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{message}</p>
  );
}

// ---------------------------------------------------------------------------
// Daily-email subscription
// ---------------------------------------------------------------------------

const SUBSCRIBE_STRIP_CLASS =
  'shrink-0 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amp-line bg-amp-white px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface';

/**
 * The one place the rolling date is explained, so the strip, the tooltip, and
 * the two states cannot drift apart (COCKPIT.md 2.3). Say this plainly: a
 * subscriber whose screen reads "1 July to 7 July" will otherwise assume the
 * email keeps arriving with that week in it.
 */
const ROLLING_DATE_NOTE =
  'The send works out the day fresh every morning, so the From and To dates on screen are not saved with the subscription.';

/**
 * Subscribe / Unsubscribe for the daily Item Sales email.
 *
 * Patterned on the Commerce > Inventory control (`CatalogInventory.tsx`), which
 * is the current pattern: it rides the GENERIC report-subscription surface
 * (`useReportSubscriptions`), where one person may hold many subscriptions to
 * the same report, each carrying its own saved filters. Retail > Performance
 * still uses the older single-boolean OMNI toggle, which cannot carry filters at
 * all.
 *
 * What is added here over the Inventory control is the Unsubscribe reach. The
 * caller's own subscriptions are already in hand from the same GET, so when one
 * of them was saved with exactly these filters the button removes it rather than
 * offering a second identical row. Anything else, including a subscription to
 * this report under different filters, still reads as Subscribe, because those
 * are two different reports as far as the subscriber is concerned. Settings
 * remains the place to see them all together.
 *
 * The date range is NOT part of what is saved (see
 * `itemSalesSubscriptionFilters`): every email covers the most recent complete
 * business day.
 *
 * Never returns null. When the subscriptions endpoint is unavailable this
 * renders DISABLED and names the problem, because a control that vanishes on
 * error is indistinguishable from one that was never built (dev, 2026-07-28).
 */
function ItemSalesSubscribeControl({ filters }: { filters: ItemSalesFilters }) {
  const { subscriptions, available } = useReportSubscriptions();
  const createMut = useCreateReportSubscription();
  const deleteMut = useDeleteReportSubscription();
  const [feedback, setFeedback] = useState<{ kind: 'saved' | 'error'; msg: string } | null>(null);

  // Clear the transient saved/error note after a few seconds.
  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 4000);
    return () => window.clearTimeout(t);
  }, [feedback]);

  const savedFilters = useMemo(() => itemSalesSubscriptionFilters(filters), [filters]);

  // The caller's existing subscription for exactly this filter set, if any.
  const existing = useMemo(() => {
    const key = itemSalesFiltersKey(savedFilters);
    return (
      subscriptions.find(
        (s) =>
          s.report_type === ITEM_SALES_REPORT_TYPE &&
          s.status === 'active' &&
          itemSalesFiltersKey(s.filters) === key,
      ) ?? null
    );
  }, [subscriptions, savedFilters]);

  // Guard after the hooks so hook order stays stable across renders.
  if (!available) {
    return (
      <div className={SUBSCRIBE_STRIP_CLASS}>
        <span className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          The daily email for this report is unavailable right now, so you cannot subscribe. Nothing
          is wrong with the sales figures below.
        </span>
        <div className="ml-auto">
          <button
            type="button"
            disabled
            title="The subscriptions service did not respond. Try again shortly; if it persists, tell IT."
            className="inline-flex cursor-not-allowed items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <Mail className="h-3 w-3" aria-hidden />
            Subscribe
          </button>
        </div>
      </div>
    );
  }

  const isBusy = createMut.isPending || deleteMut.isPending;
  const subscribed = existing !== null;

  const handleClick = () => {
    if (isBusy) return;
    if (existing) {
      deleteMut.mutate(existing.id, {
        onSuccess: () => setFeedback({ kind: 'saved', msg: 'Unsubscribed' }),
        onError: (e) =>
          setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not unsubscribe' }),
      });
      return;
    }
    createMut.mutate(
      { reportType: ITEM_SALES_REPORT_TYPE, filters: savedFilters },
      {
        onSuccess: () => setFeedback({ kind: 'saved', msg: 'Subscribed. Manage it in Settings.' }),
        onError: (e) =>
          setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not subscribe' }),
      },
    );
  };

  const Icon = isBusy ? Loader2 : subscribed ? MailCheck : Mail;
  const buttonLabel = isBusy
    ? subscribed
      ? 'Unsubscribing...'
      : 'Subscribing...'
    : subscribed
      ? 'Unsubscribe'
      : 'Subscribe';
  const buttonTitle = subscribed
    ? `This email arrives every morning with the filters saved on it. Click to stop. ${ROLLING_DATE_NOTE}`
    : `Saves the store, category, subcategory, SKU, and item-type filters you have applied. ${ROLLING_DATE_NOTE}`;

  return (
    <div className={SUBSCRIBE_STRIP_CLASS}>
      {/* Mobile keeps the strip to two clamped lines and drops the rolling-date
          note, which is also on the button's tooltip. On a phone this block was
          six lines of helper text above the grid. Full text from `md` up. */}
      <div className="min-w-[280px] flex-1">
        <p className="line-clamp-2 text-sm text-amp-ink md:line-clamp-none dark:text-amp-dark-ink">
          {subscribed
            ? 'You get this report by email every morning. Each email covers the most recent complete business day, with the filters saved on this subscription.'
            : 'Get this report by email every morning. Each email covers the most recent complete business day, with the store, category, subcategory, SKU, and item-type filters you have applied here.'}
        </p>
        <p className="hidden text-[12px] text-amp-ink-muted md:block dark:text-amp-dark-ink-muted">
          {ROLLING_DATE_NOTE}
        </p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {feedback && (
          <span
            role="status"
            className={
              feedback.kind === 'error'
                ? 'text-[12px] font-semibold text-red-700 dark:text-red-300'
                : 'inline-flex items-center gap-1 text-[12px] font-semibold text-amp-ink dark:text-amp-dark-ink'
            }
          >
            {feedback.kind === 'saved' && <Check className="h-3 w-3" aria-hidden />}
            {feedback.msg}
          </span>
        )}
        <Link
          to="/settings"
          className="text-[12px] text-amp-ink-muted underline hover:text-amp-ink hover:no-underline dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          Manage
        </Link>
        <button
          type="button"
          onClick={handleClick}
          disabled={isBusy}
          title={buttonTitle}
          className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          <Icon className={`h-3 w-3 ${isBusy ? 'animate-spin' : ''}`} aria-hidden />
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function ItemSalesInner() {
  const { user } = useSession();
  const canView = canViewItemSales(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();

  // The range. `date` is the first version's single-day param: an old bookmark
  // lands on that one day at both ends rather than silently jumping to
  // yesterday.
  const { date_from: dateFrom, date_to: dateTo } = useMemo(
    () =>
      resolveItemSalesRange(
        searchParams.get('date_from'),
        searchParams.get('date_to'),
        searchParams.get('date'),
        defaultDate(),
      ),
    [searchParams],
  );

  const itemTypePreset = itemTypePresetFromParam(searchParams.get('item_types'));
  const q = searchParams.get('q') ?? '';
  // Repeatable params, so a multi-select view is a shareable URL. Category and
  // subcategory names can contain commas, so they repeat rather than joining
  // into one value; the hook comma-joins them for the api.
  const storeNos = useMemo(() => searchParams.getAll('store_no'), [searchParams]);
  const categorySel = useMemo(() => searchParams.getAll('category'), [searchParams]);
  const subcategorySel = useMemo(() => searchParams.getAll('subcategory'), [searchParams]);

  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Writes one end of the range. The legacy `date` param is dropped on the
   * first edit so the URL carries exactly one source of truth from then on.
   */
  const setRangeEnd = (which: 'date_from' | 'date_to') => (v: string) =>
    update((sp) => {
      sp.delete('date');
      const other = which === 'date_from' ? 'date_to' : 'date_from';
      if (!sp.get(other)) sp.set(other, which === 'date_from' ? dateTo : dateFrom);
      if (v) sp.set(which, v);
      else sp.delete(which);
    });

  const setItemTypePreset = (p: ItemTypePreset) =>
    update((sp) => sp.set('item_types', itemTypeCodesFor(p).join(',')));

  const setQ = (v: string) => update((sp) => (v ? sp.set('q', v) : sp.delete('q')));

  const setRepeatable = (key: string, next: string[]) =>
    update((sp) => {
      sp.delete(key);
      for (const v of next) sp.append(key, v);
    });

  const filters: ItemSalesFilters = useMemo(
    () => ({
      date_from: dateFrom,
      date_to: dateTo,
      item_types: itemTypeCodesFor(itemTypePreset),
      store_no: storeNos.length > 0 ? storeNos : undefined,
      category: categorySel.length > 0 ? categorySel : undefined,
      subcategory: subcategorySel.length > 0 ? subcategorySel : undefined,
      q: q || undefined,
    }),
    [dateFrom, dateTo, itemTypePreset, storeNos, categorySel, subcategorySel, q],
  );

  // Sort is owned by the backend (SortableHeader is presentational, and sorting
  // an infinite-scroll cursor list client-side would reorder only the pages
  // loaded so far). Encoded in the URL as `sort=<column>:<dir>`.
  const sort = useMemo<ItemSalesSort | null>(
    () => parseItemSalesSort(searchParams.get('sort')),
    [searchParams],
  );

  const salesQ = useItemSales(filters, sort, canView);

  const toggleSort = (col: ItemSalesSortColumn) => {
    if (!sort || sort.column !== col) update((sp) => sp.set('sort', `${col}:asc`));
    else if (sort.dir === 'asc') update((sp) => sp.set('sort', `${col}:desc`));
    else update((sp) => sp.delete('sort'));
  };
  const sortedFor = (col: ItemSalesSortColumn): SortDir | null =>
    sort && sort.column === col ? sort.dir : null;

  /**
   * The mobile Sort control writes the SAME `sort` param the desktop column
   * headers write, so the URL, the grid order, and the Excel export stay in
   * step whichever control the operator used. Choosing a column keeps whatever
   * direction is already applied; the arrow button flips it.
   */
  const setSortColumn = (col: string) =>
    update((sp) => {
      if (!col) {
        sp.delete('sort');
        return;
      }
      sp.set(
        'sort',
        formatItemSalesSort({ column: col as ItemSalesSortColumn, dir: sort?.dir ?? 'asc' }),
      );
    });
  const flipSortDir = () => {
    if (!sort) return;
    update((sp) =>
      sp.set(
        'sort',
        formatItemSalesSort({ column: sort.column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }),
      ),
    );
  };

  /**
   * Store picker options. The response's own `stores` list is authoritative: it
   * is every store with data in the reported range, is not narrowed by the store
   * filter, and is derived from the sales table itself, which is what makes the
   * two online stores (DTC-LP, DTC-HM) selectable. The store-master read is kept
   * only as a fallback for a backend that predates the field, and it excludes
   * is_dtc = 1, so on that path the online stores appear only once rows load
   * for them.
   */
  const storesQ = useRetailStores();
  const storeOptions = useMemo(() => {
    const byNo = new Map<string, string>();
    if (salesQ.stores.length > 0) {
      for (const s of salesQ.stores) byNo.set(s.store_no, s.store_name ?? '');
    } else {
      for (const s of storesQ.stores) byNo.set(String(s.store_no), s.store_name);
      for (const r of salesQ.rows) {
        if (!byNo.has(r.store_no)) byNo.set(r.store_no, r.store_name ?? '');
      }
    }
    return [...byNo.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .map(([value, name]) => ({ value, label: formatItemStore(value, name) }));
  }, [salesQ.stores, storesQ.stores, salesQ.rows]);

  // Both taxonomy pickers read the response's `categories` pairs, which the
  // backend does not narrow by the active category / subcategory filters. That
  // is what lets the operator switch categories: a self-narrowing list would
  // hide the other 13 the moment one was picked.
  const categoryOptions = useMemo(
    () => itemSalesCategoryNames(salesQ.categories).map((c) => ({ value: c, label: c })),
    [salesQ.categories],
  );
  const subcategoryNames = useMemo(
    () => itemSalesSubcategoryNames(salesQ.categories, categorySel),
    [salesQ.categories, categorySel],
  );
  const subcategoryOptions = useMemo(
    () => subcategoryNames.map((s) => ({ value: s, label: s })),
    [subcategoryNames],
  );

  /**
   * Choosing categories also prunes any subcategory the new choice no longer
   * offers, in the same URL write, so the grid never sits behind a filter the
   * operator cannot see. Pruning is skipped until the taxonomy has loaded,
   * otherwise the first paint (empty `categories`) would clear a shared link's
   * subcategory filter.
   */
  const setCategories = (next: string[]) =>
    update((sp) => {
      sp.delete('category');
      for (const v of next) sp.append('category', v);
      if (salesQ.categories.length === 0) return;
      const stillValid = pruneItemSalesSubcategories(
        subcategorySel,
        itemSalesSubcategoryNames(salesQ.categories, next),
      );
      sp.delete('subcategory');
      for (const v of stillValid) sp.append('subcategory', v);
    });

  const resetFilters = () =>
    update((sp) =>
      ['q', 'store_no', 'category', 'subcategory', 'item_types', 'sort'].forEach((k) =>
        sp.delete(k),
      ),
    );

  async function handleDownload() {
    setExporting(true);
    setToast(null);
    try {
      await downloadItemSalesXlsx(filters, sort);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  // Human-readable page state for Support > Report an issue (ISD-357):
  // the SAME labels the filter bar shows, so a ticket reads "Merchandise
  // only" rather than the URL's raw `item_types=I`. Store selections are
  // resolved to their picker labels (e.g. "104 - Westfield Galleria at
  // Roseville") through the same `storeOptions` the picker itself renders.
  const storeLabels = useMemo(() => {
    const byValue = new Map(storeOptions.map((o) => [o.value, o.label]));
    return storeNos.map((v) => byValue.get(v) ?? v);
  }, [storeNos, storeOptions]);
  const pageStateEntries = useMemo(
    () =>
      buildItemSalesPageState({
        dateFrom,
        dateTo,
        itemTypeLabel: itemTypeLabelFor(itemTypePreset),
        storeLabels,
        categorySel,
        subcategorySel,
        q,
        sort,
      }),
    [dateFrom, dateTo, itemTypePreset, storeLabels, categorySel, subcategorySel, q, sort],
  );
  useReportPageState(pageStateEntries);

  if (!user && !salesQ.isLoading) return null; // AuthGate handles unauthenticated state

  if (user && !canView) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Item Sales</h1>
        <AccessDeniedCard />
      </div>
    );
  }

  // The backend returns as_of = null when the chosen range holds no data, which
  // is the empty state. `asOf` is undefined until the first page lands, so the
  // placeholder never flashes while loading.
  const showNotAvailable = !salesQ.isLoading && salesQ.asOf === null;
  const hasFilters =
    storeNos.length > 0 ||
    categorySel.length > 0 ||
    subcategorySel.length > 0 ||
    Boolean(q) ||
    Boolean(sort) ||
    itemTypePreset !== DEFAULT_ITEM_TYPE_PRESET;
  /**
   * Count for the shell's mobile Filters button, so a collapsed toolbar cannot
   * hide the fact that the rows are being narrowed. Sort is excluded because it
   * has its own always-visible control and is not a filter, and the date range
   * is excluded because it is always set and would make a default view read as
   * though a filter were applied.
   */
  const activeFilterCount =
    (storeNos.length > 0 ? 1 : 0) +
    (categorySel.length > 0 ? 1 : 0) +
    (subcategorySel.length > 0 ? 1 : 0) +
    (q ? 1 : 0) +
    (itemTypePreset !== DEFAULT_ITEM_TYPE_PRESET ? 1 : 0);
  const { totals } = salesQ;
  const today = toIsoDate(new Date());

  const filterBar = (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <FilterLabel htmlFor="item-sales-from">From</FilterLabel>
        <input
          id="item-sales-from"
          type="date"
          value={dateFrom}
          max={dateTo || today}
          onChange={(e) => setRangeEnd('date_from')(e.target.value)}
          className="border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </div>
      <div>
        <FilterLabel htmlFor="item-sales-to">To</FilterLabel>
        <input
          id="item-sales-to"
          type="date"
          value={dateTo}
          min={dateFrom || undefined}
          max={today}
          onChange={(e) => setRangeEnd('date_to')(e.target.value)}
          className="border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </div>
      <div>
        {/* Rendered as a div, not a <label>: the tooltip is a button, and a
            button nested inside a label steals the label's click target. */}
        <FilterLabel as="div">
          Items included
          <InfoTooltip label="which items are included">{ITEM_TYPE_HELP}</InfoTooltip>
        </FilterLabel>
        <select
          id="item-sales-item-types"
          aria-label="Items included"
          value={itemTypePreset}
          onChange={(e) => setItemTypePreset(e.target.value as ItemTypePreset)}
          className="border border-amp-line rounded px-2 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          {ITEM_TYPE_PRESETS.map((p) => (
            <option key={p.preset} value={p.preset}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <FilterLabel as="div">Store</FilterLabel>
        <SearchableSelect
          options={storeOptions}
          selected={storeNos}
          onChange={(next) => setRepeatable('store_no', next)}
          placeholder="All stores"
          ariaLabel="Filter by store"
        />
      </div>
      <div>
        <FilterLabel as="div">Category</FilterLabel>
        <SearchableSelect
          options={categoryOptions}
          selected={categorySel}
          onChange={setCategories}
          placeholder="All categories"
          ariaLabel="Filter by category"
          emptyMessage="No categories in this range."
        />
      </div>
      <div>
        <FilterLabel as="div">Subcategory</FilterLabel>
        <SearchableSelect
          options={subcategoryOptions}
          selected={subcategorySel}
          onChange={(next) => setRepeatable('subcategory', next)}
          placeholder="All subcategories"
          ariaLabel="Filter by subcategory"
          emptyMessage="No subcategories for the chosen category."
        />
      </div>
      <div className="min-w-[220px] flex-1">
        <FilterLabel htmlFor="item-sales-q">SKU or description</FilterLabel>
        <ClearableSearchInput
          id="item-sales-q"
          value={q}
          onChange={setQ}
          placeholder="SKU or description contains..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={exporting}
        title="Download these rows as Excel. The workbook follows the date range, filters, and sort on screen."
        className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
        Download
      </button>
      {hasFilters && (
        <button
          type="button"
          onClick={resetFilters}
          className="rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  const totalsFooter = (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px] text-amp-ink dark:text-amp-dark-ink">
      <span className="font-semibold">Totals for every matching row</span>
      <span>
        Units sold <span className="font-semibold">{formatItemUnits(totals.qty_sold)}</span>
      </span>
      <span>
        Net sales <span className="font-semibold">{formatItemMoney(totals.net_sales)}</span>
      </span>
      <span>
        Cost <span className="font-semibold">{formatItemMoney(totals.ext_cost)}</span>
      </span>
      <span>
        Tickets <span className="font-semibold">{formatItemTickets(totals.tkt_count)}</span>
      </span>
    </div>
  );

  /**
   * The mobile Sort control. Cards carry no clickable column headers, so this is
   * the only way to reorder on a phone, and it stays VISIBLE next to the Filters
   * button rather than collapsing with the toolbar: changing the sort is a
   * constant move, and burying it would cost two taps every time.
   */
  const sortControl = (
    <div className="flex items-center gap-1">
      <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">Sort</span>
      <select
        aria-label="Sort rows by"
        value={sort?.column ?? ''}
        onChange={(e) => setSortColumn(e.target.value)}
        className="rounded border border-amp-line bg-amp-white px-2 py-1.5 text-xs text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      >
        <option value="">Default order</option>
        {ITEM_SALES_SORT_OPTIONS.map((o) => (
          <option key={o.column} value={o.column}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={flipSortDir}
        disabled={!sort}
        aria-label={sort?.dir === 'desc' ? 'Sort ascending instead' : 'Sort descending instead'}
        title={
          sort
            ? sort.dir === 'desc'
              ? 'Sorted highest first. Click for lowest first.'
              : 'Sorted lowest first. Click for highest first.'
            : 'Pick a column to sort by first.'
        }
        className="rounded border border-amp-line p-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
      >
        {sort?.dir === 'desc' ? (
          <ArrowDown className="h-3 w-3" aria-hidden />
        ) : (
          <ArrowUp className="h-3 w-3" aria-hidden />
        )}
      </button>
    </div>
  );

  /**
   * One card per row for the phone. Nine columns do not fit, so the card leads
   * with store and SKU as its identity line, puts the description beneath it,
   * drops category and subcategory to small muted text, and lays the four
   * measures out as a labelled two-by-two block. The SKU is still the link to
   * the product page, money is still to the cent, and a missing cost or ticket
   * count still reads "--" rather than a zero, because every value goes through
   * the same formatters the table cells use.
   */
  const renderItemSalesCards = () => {
    if (salesQ.isLoading) return <CardNotice message="Loading item sales..." />;
    if (salesQ.error) return <CardNotice message="Not available yet." />;
    if (salesQ.rows.length === 0)
      return <CardNotice message="No item sales match these filters." />;
    return (
      <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
        {salesQ.rows.map((r) => (
          <li key={`${r.store_no}:${r.item_no}`} className="px-3 py-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate text-[14px] font-semibold text-amp-ink dark:text-amp-dark-ink">
                {formatItemStore(r.store_no, r.store_name)}
              </span>
              <Link
                to={`/commerce/products/${encodeURIComponent(r.item_no.trim())}`}
                className="shrink-0 font-mono text-[12px] text-amp-ink hover:underline dark:text-amp-dark-ink"
              >
                {r.item_no}
              </Link>
            </div>
            <p className="mt-0.5 text-[13px] text-amp-ink dark:text-amp-dark-ink">
              {r.descr?.trim() ? (
                r.descr
              ) : (
                <span
                  className="text-amp-ink-muted dark:text-amp-dark-ink-muted"
                  title="This item has no description in the CounterPoint item master."
                >
                  No description
                </span>
              )}
            </p>
            <p className="mt-0.5 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
              {r.category ?? '--'} / {r.subcategory ?? '--'}
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
              <CardMetric label="Units sold" value={formatItemUnits(r.qty_sold)} />
              <CardMetric label="Net sales" value={formatItemMoney(r.net_sales)} />
              <CardMetric label="Cost" value={formatItemMoney(r.ext_cost)} />
              <CardMetric label="Tickets" value={formatItemTickets(r.tkt_count)} />
            </dl>
          </li>
        ))}
      </ul>
    );
  };

  const rangeLabel =
    salesQ.dateFrom === salesQ.dateTo
      ? formatDate(salesQ.dateFrom)
      : `${formatDate(salesQ.dateFrom)} to ${formatDate(salesQ.dateTo)}`;

  return (
    <div className="h-full flex flex-col min-h-0">

      <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            Stores
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Item Sales</h1>
          {/* Clamped to two lines on a phone: at full length this ran to four
              lines and, with the subscribe strip and the filter bar, consumed
              the whole viewport, which is what squeezed the rows to zero
              height. Full text from `md` up. */}
          <p className="line-clamp-2 text-sm text-amp-ink-muted md:line-clamp-none dark:text-amp-dark-ink-muted">
            What sold, at which store, by SKU. One row per store per SKU for the whole range, with
            units, net sales, cost, and tickets for every store including the two online stores.
            Sales days {rangeLabel}. Showing {itemTypeLabelFor(itemTypePreset).toLowerCase()}.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-end gap-2">
          <ToolPinToggle toolSlug="item-sales" toolName="Item Sales" />
        </div>
      </div>

      <ItemSalesSubscribeControl filters={filters} />

      {toast && (
        <div
          className="shrink-0 mb-4 flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200"
          role="status"
        >
          {toast}
          <button type="button" onClick={() => setToast(null)} className="ml-4 text-xs">
            &times;
          </button>
        </div>
      )}

      {showNotAvailable ? (
        <>
          <div className="shrink-0 mb-4">{filterBar}</div>
          <NotAvailableCard />
        </>
      ) : (
        <DataGridShell
          total={salesQ.totalCount}
          loaded={salesQ.rows.length}
          countNoun="item rows"
          toolbar={filterBar}
          activeFilterCount={activeFilterCount}
          mobileActions={sortControl}
          renderCards={renderItemSalesCards}
          footer={totalsFooter}
          onReachEnd={salesQ.fetchNextPage}
          hasMore={salesQ.hasNextPage}
          isLoadingMore={salesQ.isFetchingNextPage}
        >
          <table className="min-w-full text-sm">
            <thead className={gridTheadClassName}>
              <tr>
                <SortableHeader
                  label="Store"
                  sorted={sortedFor('store_no')}
                  onToggle={() => toggleSort('store_no')}
                  thClassName="px-3 py-2 min-w-[180px]"
                />
                <SortableHeader
                  label="SKU"
                  sorted={sortedFor('item_no')}
                  onToggle={() => toggleSort('item_no')}
                  thClassName="px-3 py-2 w-[130px]"
                />
                <SortableHeader
                  label="Description"
                  sorted={sortedFor('descr')}
                  onToggle={() => toggleSort('descr')}
                  thClassName="px-3 py-2 min-w-[240px]"
                />
                <SortableHeader
                  label="Category"
                  sorted={sortedFor('category')}
                  onToggle={() => toggleSort('category')}
                  thClassName="px-3 py-2 min-w-[150px]"
                  tooltip="Leadership's product category for this SKU. Blank where the item carries none."
                />
                <SortableHeader
                  label="Subcategory"
                  sorted={sortedFor('subcategory')}
                  onToggle={() => toggleSort('subcategory')}
                  thClassName="px-3 py-2 min-w-[150px]"
                  tooltip="Leadership's product subcategory for this SKU. Blank where the item carries none."
                />
                <SortableHeader
                  label="Units sold"
                  sorted={sortedFor('qty_sold')}
                  onToggle={() => toggleSort('qty_sold')}
                  align="right"
                  thClassName="px-3 py-2 w-[110px]"
                />
                <SortableHeader
                  label="Net sales"
                  sorted={sortedFor('net_sales')}
                  onToggle={() => toggleSort('net_sales')}
                  align="right"
                  thClassName="px-3 py-2 w-[120px]"
                />
                <SortableHeader
                  label="Cost"
                  sorted={sortedFor('ext_cost')}
                  onToggle={() => toggleSort('ext_cost')}
                  align="right"
                  thClassName="px-3 py-2 w-[120px]"
                  tooltip="Extended cost of the units sold. Blank where cost is not populated for the item, which happens on the two online stores."
                />
                <SortableHeader
                  label="Tickets"
                  sorted={sortedFor('tkt_count')}
                  onToggle={() => toggleSort('tkt_count')}
                  align="right"
                  thClassName="px-3 py-2 w-[100px]"
                  tooltip="Number of register tickets that included this SKU across the range. Blank for the two online stores, where Shopify reports orders at the order level rather than per SKU."
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {salesQ.isLoading ? (
                <EmptyBody message="Loading item sales..." />
              ) : salesQ.error ? (
                <EmptyBody message="Not available yet." />
              ) : salesQ.rows.length === 0 ? (
                <EmptyBody message="No item sales match these filters." />
              ) : (
                salesQ.rows.map((r) => (
                  <tr
                    key={`${r.store_no}:${r.item_no}`}
                    className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                      {formatItemStore(r.store_no, r.store_name)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
                      <Link
                        to={`/commerce/products/${encodeURIComponent(r.item_no.trim())}`}
                        className="hover:underline"
                      >
                        {r.item_no}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                      {r.descr?.trim() ? (
                        r.descr
                      ) : (
                        <span
                          className="text-amp-ink-muted dark:text-amp-dark-ink-muted"
                          title="This item has no description in the CounterPoint item master."
                        >
                          No description
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                      <TaxonomyCell value={r.category} kind="category" />
                    </td>
                    <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
                      <TaxonomyCell value={r.subcategory} kind="subcategory" />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                      {formatItemUnits(r.qty_sold)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap font-medium text-amp-ink dark:text-amp-dark-ink">
                      {formatItemMoney(r.net_sales)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                      {formatItemMoney(r.ext_cost)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                      {formatItemTickets(r.tkt_count)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DataGridShell>
      )}
    </div>
  );
}

export function RetailItemSales() {
  return (
    <ErrorBoundary>
      <ItemSalesInner />
    </ErrorBoundary>
  );
}
